import { useQuery as useGQLQuery } from '@apollo/client';
import { CloudDownload } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import STUDENTS_QUERY from 'gql/queries/ModuleSelectionsService/getLatestCalculatedLotteryResultByStudents.gql';
import LATEST_LOTTERY_QUERY from 'gql/queries/ModuleSelectionsService/getLatestLotteryBySelection.gql';

import { useModulesQuery } from 'api/hooks/modules';
import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { DataGrid } from 'app/components/DataGrid';
import { Filter, Filters } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import {
  Lottery,
  LotteryResult,
  StudentSelection
} from 'app/containers/AWPM-FWPM/StudentView/models';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { ModuleSelection, SubjectType } from '../../models';
import { PreCalculationControl } from '../components/PreCalculationControl';
import '../styles/ModuleSelectionsDetail.scss';

type GetStudentResultsQueryResult = { getLatestCalculatedLotteryResultByStudents: any };
type GetLatestLotteryQueryResult = { getLatestLotteryBySelection: Lottery };

type Props = {
  moduleSelection: ModuleSelection;
  subjectType: SubjectType;
};

export const StudentList = ({ moduleSelection, subjectType }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isInsufficientModuleFilterActive = useState<boolean>();
  const courseOfStudyFilterStateHandler = useState([]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getStudentResults = useGQLQuery<GetStudentResultsQueryResult>(STUDENTS_QUERY, {
    variables: { moduleSelectionId: parseInt(moduleSelection.id) },
    context: { clientName: 'federation' },
    fetchPolicy: 'network-only'
  });

  const { data: courses } = useCoursesQuery();

  const getLatestLottery = useGQLQuery<GetLatestLotteryQueryResult, { moduleSelectionId: number }>(
    LATEST_LOTTERY_QUERY,
    {
      variables: { moduleSelectionId: Number(moduleSelection.id) },
      context: { clientName: 'federation' },
      fetchPolicy: 'network-only'
    }
  );

  const { data: modules, isLoading } = useModulesQuery({
    params: {
      selectionId: moduleSelection.id,
      nameSort: 'asc'
    }
  });

  const controlElements = () => {
    return (
      <div>
        <Button startIcon={<CloudDownload />} variant="outlined" onClick={handleClick}>
          {t(translations.common.export())}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          sx={{ width: 300 }}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
        >
          <MenuItem
            onClick={() =>
              handleDownload(
                'students.xlsx',
                `${import.meta.env.VITE_GATEWAY_URL}/exports/module-selections/${
                  moduleSelection.id
                }/students`
              )
            }
          >
            <ListItemIcon className="student-list__export">
              <CloudDownload />
            </ListItemIcon>
            <Typography noWrap className="student-list__export">
              {t(translations.pages.moduleSelectionsDetail.studentList.export.exportAllStudents())}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleDownload(
                'students-without-received-modules.xlsx',
                `${import.meta.env.VITE_GATEWAY_URL}/exports/module-selections/${
                  moduleSelection.id
                }/students-without-received-modules`
              )
            }
          >
            <ListItemIcon className="student-list__export">
              <CloudDownload />
            </ListItemIcon>
            <Tooltip
              title={t(
                translations.pages.moduleSelectionsDetail.studentList.export.exportAllStudentsWithoutModules()
              )}
            >
              <Typography noWrap className="student-list__export">
                {t(
                  translations.pages.moduleSelectionsDetail.studentList.export.exportAllStudentsWithoutModules()
                )}
              </Typography>
            </Tooltip>
          </MenuItem>
        </Menu>
      </div>
    );
  };

  const richlistFilter = (): Filter[] => [
    {
      field: 'receivedModules',
      label: t(
        translations.pages.moduleSelectionsDetail.studentList.filter.studentsWithInsufficientModules()
      ),
      stateHandler: isInsufficientModuleFilterActive,
      switch: {
        label: t(
          translations.pages.moduleSelectionsDetail.studentList.filter.studentsWithInsufficientModules()
        ),
        stateHandler: isInsufficientModuleFilterActive,
        labelPlacement: 'top'
      }
    },
    {
      field: 'courseOfStudy',
      label: t(translations.pages.moduleSelectionsDetail.filter.courseOfStudy()),
      multiselect: {
        options: [
          ...courses
            .map(course => ({
              label: `(${course.shorthand}) ${course.name}`,
              name: course.id
            }))
            .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
        ],
        customStyle: { width: '200px' },
        stateHandler: courseOfStudyFilterStateHandler
      }
    }
  ];

  const onFilterChange = variables => {
    const parsedFilter = JSON.parse(variables.filter);

    const modifiedFilter: Record<string, any> = {
      receivedModules: undefined,
      courseOfStudy: undefined
    };

    Object.keys(parsedFilter).forEach(key => {
      modifiedFilter[key] = parsedFilter[key].term;
    });

    getStudentResults.refetch(modifiedFilter);
  };

  const columns = useMemo(
    (): GridColDef[] => [
      {
        flex: 2,
        minWidth: 150,
        headerName: t(translations.pages.moduleSelectionsDetail.studentList.table.mtrNumber()),
        field: 'uniqueCode',
        renderCell: params => (
          <div className="rowElement">
            <Tooltip title={params.row.uniqueCode}>
              <span>{params.row.uniqueCode}</span>
            </Tooltip>
          </div>
        ),
        sortable: false
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.moduleSelectionsDetail.studentList.table.firstName()),
        field: 'firstName',
        renderCell: params => (
          <div className="rowElement">
            <Tooltip title={params.row.firstName}>
              <span>{params.row.firstName}</span>
            </Tooltip>
          </div>
        ),
        sortable: false
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.moduleSelectionsDetail.studentList.table.lastName()),
        field: 'lastName',
        renderCell: params => (
          <div className="rowElement">
            <Tooltip title={params.row.lastName}>
              <div>{params.row.lastName}</div>
            </Tooltip>
          </div>
        ),
        sortable: false
      },
      {
        flex: 1,
        minWidth: 170,
        headerName: t(translations.pages.moduleSelectionsDetail.studentList.table.courseOfStudy()),
        field: 'courseOfStudyId',
        renderCell: params => {
          const title = courses.find(item => item.id === params.row.courseOfStudyId)?.name || '';
          return (
            <div className="rowElement">
              <Tooltip title={title}>
                <span>{title}</span>
              </Tooltip>
            </div>
          );
        },
        sortable: false
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.pages.moduleSelectionsDetail.studentList.table.semester()),
        field: 'semester',
        sortable: false,
        renderCell: params => <span className="rowElement">{params.row.semester}</span>
      },
      {
        flex: 3,
        minWidth: 200,
        headerName: t(
          translations.pages.moduleSelectionsDetail.studentList.table.modulePreferences()
        ),
        field: 'modulePreferences',
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<
            { studentSelection: StudentSelection },
            { studentSelection: StudentSelection }
          >
        ) => {
          return isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <span className="rowElement">
              {params.row.studentSelection?.modulePreferences?.map((modulePreference, index) => {
                const title: string =
                  modules?.find(module => module.id === modulePreference.moduleId)?.name ?? '';
                const isFirstElement = index === 0;
                return (
                  <Grid item key={index} className={!isFirstElement ? 'listedElement' : ''}>
                    {!isFirstElement && <Divider />}
                    <Tooltip title={title} disableInteractive>
                      <span>{title}</span>
                    </Tooltip>
                  </Grid>
                );
              })}
            </span>
          );
        }
      },
      {
        flex: 1,
        minWidth: 200,
        headerName: t(
          translations.pages.moduleSelectionsDetail.studentList.table.desiredNumberOfModules()
        ),
        field: 'desiredNumberOfModules',
        sortable: false,
        renderCell: params => (
          <span className="rowElement">{params.row.studentSelection.desiredNumberOfModules}</span>
        )
      },
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.moduleSelectionsDetail.studentList.table.lotteryResult()),
        field: 'lotteryResults',
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<
            { lotteryResults: LotteryResult[] },
            { lotteryResults: LotteryResult[] }
          >
        ) => {
          return isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <span className="rowElement">
              {params.row.lotteryResults
                ?.filter(lotteryResult => lotteryResult.assignedToModule)
                .map((lotteryResult, index) => {
                  const title: string =
                    modules?.find(module => module.id === lotteryResult.modulePreference?.moduleId)
                      ?.name ?? '';
                  const isFirstElement = index === 0;
                  return (
                    <Grid item key={index} className={!isFirstElement ? 'listedElement' : ''}>
                      {!isFirstElement && <Divider />}
                      <Tooltip title={title} disableInteractive>
                        <span>{title}</span>
                      </Tooltip>
                      {!!lotteryResult.signedOut && (
                        <Tooltip
                          title={t(
                            translations.pages.moduleSelectionsDetail.studentList.table.studentHasBeenRemoved()
                          )}
                          disableInteractive
                        >
                          <CloseIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Grid>
                  );
                })}
            </span>
          );
        }
      }
    ],
    [courses, modules, t, isLoading]
  );

  return (
    <GQLGuard
      query={[getStudentResults, getLatestLottery]}
      onError={error => {
        if (error?.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`));
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.modules.alerts.failedLoading())}: ${error?.message ?? error}`
            )
          );
        }
        navigate('/', { replace: true });
        return null;
      }}
    >
      {([
        { getLatestCalculatedLotteryResultByStudents },
        { getLatestLotteryBySelection: latestLotteryData }
      ]: [GetStudentResultsQueryResult, GetLatestLotteryQueryResult]) => (
        <Grid container>
          <PreCalculationControl
            moduleSelection={moduleSelection}
            latestLotteryData={latestLotteryData}
          />
          <Grid container marginRight={'1em'} marginLeft={'1em'}>
            <Filters
              filterConfig={richlistFilter()}
              onFilterChange={onFilterChange}
              controls={controlElements()}
            />
          </Grid>
          <Grid container>
            <Grid
              item
              flexGrow={1}
              style={{ height: `70vh`, width: '100%' }}
              component={Paper}
              padding={2}
              className="student-list"
            >
              <DataGrid
                style={{ height: '100%', width: '100%' }}
                disableColumnFilter
                disableRowSelectionOnClick
                columns={columns}
                getRowHeight={() => 'auto'}
                rows={getLatestCalculatedLotteryResultByStudents}
                paginationMode={'client'}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </GQLGuard>
  );
};
